import React, { useMemo, useState } from 'react';
import { Pie } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, ChartOptions } from 'chart.js';
import { observer } from 'mobx-react-lite';
import { COTDistributionApiResponse } from 'Interfaces/ProductWikiInterfaces/cotDistribution.types';
import './COTDistributionChart.styles.scss';

// Register Chart.js components for Pie chart
ChartJS.register(ArcElement, Tooltip, Legend);

// Official Brand Color Palette for Agency Distribution chart
// Colors from official brand style guide (Pantone) - matching other charts
const BRAND_COLORS = [
  'rgba(82, 45, 110, 0.85)', // Brand Purple (Pantone 269 C) - #522D6E
  'rgba(199, 146, 19, 0.85)', // Brand Gold (Pantone 1245 C) - #C79213
  'rgba(2, 153, 153, 0.85)', // Brand Teal - #029999
  'rgba(153, 7, 153, 0.85)', // Brand Magenta - #990799
  'rgba(120, 86, 135, 0.85)', // Brand Light Purple - #785687
  'rgba(123, 75, 159, 0.85)', // Secondary Purple - #7B4B9F
  'rgba(77, 46, 109, 0.85)', // Primary Light - #4d2e6d
  'rgba(100, 116, 139, 0.85)', // Slate gray for additional items
];

// Solid border colors for better definition
const BRAND_BORDER_COLORS = [
  '#522D6E', // Brand Purple
  '#C79213', // Brand Gold
  '#029999', // Brand Teal
  '#990799', // Brand Magenta
  '#785687', // Brand Light Purple
  '#7B4B9F', // Secondary Purple
  '#4d2e6d', // Primary Light
  '#64748b', // Slate gray
];

interface COTDistributionChartProps {
  data: COTDistributionApiResponse | null;
  isLoading?: boolean;
  error?: string | null;
}

type ViewType = 'revenue' | 'volume';

const COTDistributionChart: React.FC<COTDistributionChartProps> = observer(
  ({ data, isLoading = false, error = null }) => {
    const [activeView, setActiveView] = useState<ViewType>('revenue');

    // Format large numbers for display
    const formatValue = (value: number, type: ViewType): string => {
      if (type === 'revenue') {
        if (value >= 1_000_000) {
          return `$${(value / 1_000_000).toFixed(1)}M`;
        } else if (value >= 1_000) {
          return `$${(value / 1_000).toFixed(1)}K`;
        }
        return `$${value.toFixed(0)}`;
      } else {
        if (value >= 1_000_000) {
          return `${(value / 1_000_000).toFixed(1)}M`;
        } else if (value >= 1_000) {
          return `${(value / 1_000).toFixed(1)}K`;
        }
        return value.toFixed(0);
      }
    };

    // Prepare chart data based on active view
    const chartData = useMemo(() => {
      if (!data || !data.data || data.data.length === 0) {
        return null;
      }

      const labels = data.data.map(item => item.cot);
      const values =
        activeView === 'revenue'
          ? data.data.map(item => item.revenue)
          : data.data.map(item => item.volume);

      // Use brand colors instead of API colors for consistency
      const backgroundColors = data.data.map(
        (_, index) => BRAND_COLORS[index % BRAND_COLORS.length]
      );
      const borderColors = data.data.map(
        (_, index) => BRAND_BORDER_COLORS[index % BRAND_BORDER_COLORS.length]
      );

      return {
        labels,
        datasets: [
          {
            data: values,
            backgroundColor: backgroundColors,
            borderColor: borderColors,
            borderWidth: 2,
            hoverBorderWidth: 3,
            hoverOffset: 8,
          },
        ],
      };
    }, [data, activeView]);

    // Chart options
    const chartOptions: ChartOptions<'pie'> = useMemo(
      () => ({
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: true,
            position: 'right',
            align: 'center',
            labels: {
              usePointStyle: true,
              pointStyle: 'circle',
              padding: 12,
              font: {
                size: 15,
              },
              color: '#374151',
              generateLabels: chart => {
                const datasets = chart.data.datasets;
                const labels = chart.data.labels as string[];
                if (datasets.length === 0 || !labels) return [];

                return labels.map((label, i) => {
                  const percentage =
                    activeView === 'revenue'
                      ? data?.data[i]?.revenuePercentage || 0
                      : data?.data[i]?.volumePercentage || 0;
                  // Use brand colors for legend
                  const bgColor = BRAND_COLORS[i % BRAND_COLORS.length];
                  const borderColor = BRAND_BORDER_COLORS[i % BRAND_BORDER_COLORS.length];

                  return {
                    text: `${label} (${percentage.toFixed(1)}%)`,
                    fillStyle: bgColor,
                    strokeStyle: borderColor,
                    lineWidth: 2,
                    hidden: false,
                    index: i,
                    pointStyle: 'circle',
                  };
                });
              },
            },
          },
          tooltip: {
            enabled: true,
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            titleColor: '#374151',
            bodyColor: '#374151',
            borderColor: '#e5e7eb',
            borderWidth: 1,
            padding: 12,
            callbacks: {
              label: context => {
                const value = context.parsed;
                const index = context.dataIndex;
                const percentage =
                  activeView === 'revenue'
                    ? data?.data[index]?.revenuePercentage || 0
                    : data?.data[index]?.volumePercentage || 0;

                return `${context.label}: ${formatValue(value, activeView)} (${percentage.toFixed(1)}%)`;
              },
            },
          },
        },
      }),
      [activeView, data]
    );

    // Render loading state
    if (isLoading) {
      return (
        <div className="cot-distribution-chart">
          <div className="cot-chart-header">
            <h4 className="cot-chart-title">Agency Distribution</h4>
          </div>
          <div className="cot-chart-content loading">
            <div className="loading-spinner"></div>
            <span>Loading distribution data...</span>
          </div>
        </div>
      );
    }

    // Render error state
    if (error) {
      return (
        <div className="cot-distribution-chart">
          <div className="cot-chart-header">
            <h4 className="cot-chart-title">Agency Distribution</h4>
          </div>
          <div className="cot-chart-content error">
            <span className="error-message">{error}</span>
          </div>
        </div>
      );
    }

    // Render no data state
    if (!data || !data.data || data.data.length === 0) {
      return (
        <div className="cot-distribution-chart">
          <div className="cot-chart-header">
            <h4 className="cot-chart-title">Agency Distribution</h4>
          </div>
          <div className="cot-chart-content no-data">
            <span>No agency distribution data available</span>
          </div>
        </div>
      );
    }

    return (
      <div className="cot-distribution-chart">
        <div className="cot-chart-header">
          <h4 className="cot-chart-title">Agency Distribution</h4>
          <div className="cot-view-toggle">
            <button
              className={`toggle-btn ${activeView === 'revenue' ? 'active' : ''}`}
              onClick={() => setActiveView('revenue')}
            >
              Revenue
            </button>
            <button
              className={`toggle-btn ${activeView === 'volume' ? 'active' : ''}`}
              onClick={() => setActiveView('volume')}
            >
              Volume
            </button>
          </div>
        </div>

        <div className="cot-chart-content">
          <div className="pie-chart-container">
            {chartData && <Pie data={chartData} options={chartOptions} />}
          </div>

          {/* <div className="cot-chart-summary">
            <div className="summary-item">
              <span className="summary-label">
                Total {activeView === 'revenue' ? 'Revenue' : 'Volume'}:
              </span>
              <span className="summary-value">
                {formatValue(
                  activeView === 'revenue' ? data.totalRevenue : data.totalVolume,
                  activeView
                )}
              </span>
            </div>
            <div className="summary-item">
              <span className="summary-label">Agencies:</span>
              <span className="summary-value">{data.data.length}</span>
            </div>
          </div> */}
        </div>
      </div>
    );
  }
);

COTDistributionChart.displayName = 'COTDistributionChart';

export default COTDistributionChart;
